/**
 * Closed-form shoebox modal pressure — same φ_nx,ny,nz cos product as
 * backend/modal_analysis.py, trimmed for a public 2D/3D heatmap tool.
 */

import { calculateRoomModes, type RoomMode } from '@/lib/acousticsPhysics';

export interface RoomBox {
  lengthM: number;
  widthM: number;
  heightM: number;
}

export interface Point3 {
  x: number;
  y: number;
  z: number;
}

/** Mode shape φ at a point (x along length, y along width, z up). */
export function modeShapeAt(
  mode: Pick<RoomMode, 'nx' | 'ny' | 'nz'>,
  room: RoomBox,
  x: number,
  y: number,
  z: number,
): number {
  const { lengthM: L, widthM: W, heightM: H } = room;
  return (
    Math.cos((mode.nx * Math.PI * x) / L) *
    Math.cos((mode.ny * Math.PI * y) / W) *
    Math.cos((mode.nz * Math.PI * z) / H)
  );
}

export function absPressureAt(
  mode: Pick<RoomMode, 'nx' | 'ny' | 'nz'>,
  room: RoomBox,
  x: number,
  y: number,
  z: number,
): number {
  return Math.abs(modeShapeAt(mode, room, x, y, z));
}

export interface PressureGrid {
  nx: number;
  ny: number;
  /** Row-major |φ| samples on the floor plane (z = earHeight). */
  values: Float32Array;
  min: number;
  max: number;
}

/** Floor-plane pressure heatmap at ear height for one mode. */
export function pressureFieldFloor(
  mode: Pick<RoomMode, 'nx' | 'ny' | 'nz'>,
  room: RoomBox,
  gridX = 48,
  gridY = 36,
  earHeightM = 1.2,
): PressureGrid {
  const values = new Float32Array(gridX * gridY);
  let min = Infinity;
  let max = -Infinity;
  for (let j = 0; j < gridY; j++) {
    const y = (j / (gridY - 1)) * room.widthM;
    for (let i = 0; i < gridX; i++) {
      const x = (i / (gridX - 1)) * room.lengthM;
      const p = absPressureAt(mode, room, x, y, earHeightM);
      values[j * gridX + i] = p;
      if (p < min) min = p;
      if (p > max) max = p;
    }
  }
  return { nx: gridX, ny: gridY, values, min, max };
}

const NULL_THRESHOLD = 0.12;
/** Soft floor for auralization only — never apply to chart magnitudes. */
export const MODAL_AUDIO_FLOOR = 0.05;

export function isPressureNull(pressure: number): boolean {
  return pressure <= NULL_THRESHOLD;
}

export function findNearestMode(modes: RoomMode[], targetHz: number): RoomMode | null {
  if (modes.length === 0) return null;
  return modes.reduce((best, m) =>
    Math.abs(m.freqHz - targetHz) < Math.abs(best.freqHz - targetHz) ? m : best,
  );
}

export interface ModalSeatResponse {
  /** Raw linear |H(f)| for charts (no floor). */
  mag: Float64Array;
  /** Floored copy safe for auralization IR synth. */
  magAudio: number[];
  /** Resonant frequencies included in the sum. */
  modeFreqsHz: number[];
}

/**
 * Approximate modal transfer at a listening seat (source at speaker).
 * Vectorized over frequency for each mode (typed arrays, mode-outer loop).
 *
 * H(f) ≈ Σ_n  a_n / ((f²−f_n²) + j·f·f_n/Q),  a_n = φ_s·φ_l
 */
export function computeModalSeatResponse(
  freqs: ArrayLike<number>,
  room: RoomBox,
  speaker: Point3,
  listener: Point3,
  maxHz = 250,
  Q = 12,
): ModalSeatResponse {
  const modes = calculateRoomModes(room.lengthM, room.widthM, room.heightM, maxHz);
  const nM = modes.length;
  const nF = freqs.length;

  const fn = new Float64Array(nM);
  const fn2 = new Float64Array(nM);
  const a = new Float64Array(nM);
  const modeFreqsHz: number[] = new Array(nM);

  for (let k = 0; k < nM; k++) {
    const m = modes[k];
    modeFreqsHz[k] = m.freqHz;
    fn[k] = m.freqHz;
    fn2[k] = m.freqHz * m.freqHz;
    a[k] =
      modeShapeAt(m, room, speaker.x, speaker.y, speaker.z) *
      modeShapeAt(m, room, listener.x, listener.y, listener.z);
  }

  const re = new Float64Array(nF);
  const im = new Float64Array(nF);
  const fArr = freqs instanceof Float64Array ? freqs : Float64Array.from(freqs as ArrayLike<number>);

  for (let k = 0; k < nM; k++) {
    const ak = a[k];
    if (Math.abs(ak) < 1e-6) continue;
    const fnk = fn[k];
    const fn2k = fn2[k];
    for (let i = 0; i < nF; i++) {
      const f = fArr[i];
      const denomRe = f * f - fn2k;
      const denomIm = (f * fnk) / Q;
      const inv = 1 / (denomRe * denomRe + denomIm * denomIm);
      re[i] += ak * denomRe * inv;
      im[i] -= ak * denomIm * inv;
    }
  }

  const mag = new Float64Array(nF);
  const magAudio = new Array<number>(nF);
  for (let i = 0; i < nF; i++) {
    const m = Math.hypot(re[i], im[i]);
    mag[i] = m;
    magAudio[i] = Math.max(MODAL_AUDIO_FLOOR, m);
  }

  return { mag, magAudio, modeFreqsHz };
}

/**
 * Back-compat: linear mag with audio floor (for older callers / auralization).
 * Prefer `computeModalSeatResponse` when you need a chart curve.
 */
export function modalSeatMagnitude(
  freqs: number[],
  room: RoomBox,
  speaker: Point3,
  listener: Point3,
  maxHz = 250,
  Q = 12,
): number[] {
  return computeModalSeatResponse(freqs, room, speaker, listener, maxHz, Q).magAudio;
}

/** Mean-centered dB for REW-style relative plots (pure flat → 0 dB). */
export function relativeDb(mag: ArrayLike<number>): number[] {
  const n = mag.length;
  if (n === 0) return [];
  const db = new Array<number>(n);
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const d = 20 * Math.log10(Math.max(mag[i], 1e-9));
    db[i] = d;
    sum += d;
  }
  const mean = sum / n;
  for (let i = 0; i < n; i++) db[i] -= mean;
  return db;
}
